import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AuthButton, Button, getConnectOptionsFromStrategy, Input, isWalletType, LoginStepContainer, useAuthentication, useAuthStore, useB3Config, useConnect, WalletRow } from "../../../../../global-account/react/index.js";
import { ecosystemWalletId } from "../../../../../shared/constants/index.js";
import { debugB3React } from "../../../../../shared/utils/debug.js";
import { client } from "../../../../../shared/utils/thirdweb.js";
import { useState } from "react";
import { useConnectedWallets, useConnect as useConnectTW } from "thirdweb/react";
import { createWallet, preAuthenticate } from "thirdweb/wallets";
const debug = debugB3React("LoginStepCustom");
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function LoginStepCustom({
  onSuccess,
  onError,
  chain,
  strategies,
  maxInitialWallets = 2,
  automaticallySetFirstEoa
}) {
  const {
    partnerId
  } = useB3Config();
  const [isLoading, setIsLoading] = useState(false);
  const [showAllWallets, setShowAllWallets] = useState(false);
  const [showEmailFlow, setShowEmailFlow] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const {
    connect
  } = useConnect(partnerId, chain);
  const setIsAuthenticating = useAuthStore(state => state.setIsAuthenticating);
  const {
    connect: onAuthConnect,
    logout
  } = useAuthentication(partnerId, {
    skipAutoConnect: true
  });
  const {
    connect: connectTW
  } = useConnectTW();
  const connectedWallets = useConnectedWallets();
  // Split strategies into auth and wallet types
  const authStrategies = strategies.filter(s => !isWalletType(s));
  const walletStrategies = strategies.filter(isWalletType);
  const initialWallets = walletStrategies.slice(0, maxInitialWallets);
  const additionalWallets = walletStrategies.slice(maxInitialWallets);
  const authGridColumns = Math.max(1, Math.min(authStrategies.length, 4));
  const resetEmailFlow = () => {
    setShowEmailFlow(false);
    setEmailCodeSent(false);
    setVerificationCode("");
    setEmailError(null);
  };
  const connectWithOptions = async (strategy, options) => {
    try {
      setIsLoading(true);
      debug("setIsAuthenticating:true:3");
      setIsAuthenticating(true);
      let connectResult;
      if (automaticallySetFirstEoa && isWalletType(strategy) && options.strategy === "wallet") {
        const walletId = options.wallet?.id;
        if (!walletId) {
          throw new Error("Wallet ID is required");
        }
        connectResult = await connectTW(async () => {
          const wallet = createWallet(walletId);
          await wallet.connect({
            client
          });
          return wallet;
        });
      } else {
        connectResult = await connect(options);
      }
      const account = connectResult?.getAccount();
      debug("@@connectResult", {
        connectResult,
        account,
        options
      });
      if (!account || !connectResult) throw new Error("Failed to connect");
      const allConnectedWallets = connectedWallets.length > 0 && connectedWallets.some(wallet_1 => wallet_1.id === connectResult.id) ? connectedWallets : [connectResult, ...connectedWallets.filter(wallet_0 => wallet_0.id !== connectResult.id)];
      await onAuthConnect(connectResult, allConnectedWallets);
      await onSuccess(account);
      if (strategy === "email") {
        resetEmailFlow();
      }
    } catch (error) {
      if (strategy === "email") {
        setEmailError(error instanceof Error ? error.message : "Failed to sign in with email");
      }
      await onError?.(error);
      await logout();
    } finally {
      setIsLoading(false);
      debug("setIsAuthenticating:false:3");
      setIsAuthenticating(false);
    }
  };
  const handleConnect = async strategy_0 => {
    if (strategy_0 === "email") {
      setShowEmailFlow(true);
      setEmailCodeSent(false);
      setVerificationCode("");
      setEmailError(null);
      return;
    }
    const options_0 = getConnectOptionsFromStrategy(strategy_0);
    await connectWithOptions(strategy_0, options_0);
  };
  const handleSendEmailCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailError("Please enter your email address");
      return;
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    try {
      setIsLoading(true);
      setEmailError(null);
      await preAuthenticate({
        client,
        strategy: "email",
        email: normalizedEmail,
        ecosystem: {
          id: ecosystemWalletId,
          partnerId
        }
      });
      setEmail(normalizedEmail);
      setEmailCodeSent(true);
    } catch (error_0) {
      setEmailError(error_0 instanceof Error ? error_0.message : "Failed to send verification code");
      await onError?.(error_0);
    } finally {
      setIsLoading(false);
    }
  };
  const handleEmailLogin = async () => {
    const normalizedEmail_0 = email.trim().toLowerCase();
    const normalizedCode = verificationCode.trim();
    if (!EMAIL_REGEX.test(normalizedEmail_0)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!normalizedCode) {
      setEmailError("Please enter your verification code");
      return;
    }
    await connectWithOptions("email", {
      strategy: "email",
      email: normalizedEmail_0,
      verificationCode: normalizedCode
    });
  };
  return _jsx(LoginStepContainer, {
    partnerId: partnerId,
    children: showEmailFlow ? _jsxs("div", {
      className: "mb-6 w-full space-y-3 px-3",
      children: [_jsx("p", {
        className: "text-center text-sm font-medium text-gray-900 dark:text-gray-100",
        children: "Sign in with email"
      }), _jsx(Input, {
        type: "email",
        placeholder: "you@example.com",
        value: email,
        onChange: event => setEmail(event.target.value),
        disabled: isLoading || emailCodeSent
      }), emailCodeSent && _jsx(Input, {
        type: "text",
        placeholder: "Enter verification code",
        value: verificationCode,
        onChange: event_0 => setVerificationCode(event_0.target.value),
        disabled: isLoading
      }), emailError && _jsx("p", {
        className: "text-sm text-red-500",
        children: emailError
      }), _jsx(Button, {
        onClick: emailCodeSent ? handleEmailLogin : handleSendEmailCode,
        disabled: isLoading,
        className: "w-full",
        children: isLoading ? "Loading..." : emailCodeSent ? "Verify code" : "Send code"
      }), emailCodeSent && _jsx(Button, {
        variant: "outline",
        onClick: handleSendEmailCode,
        disabled: isLoading,
        className: "w-full",
        children: "Resend code"
      }), _jsx(Button, {
        variant: "outline",
        onClick: resetEmailFlow,
        disabled: isLoading,
        className: "w-full",
        children: "Back"
      })]
    }) : _jsxs(_Fragment, {
      children: [authStrategies.length > 0 && _jsx("div", {
        className: `mb-6 grid w-full gap-4 px-3 ${authStrategies.length > 4 ? "grid-cols-4" : ""}`,
        style: authStrategies.length <= 4 ? {
          gridTemplateColumns: `repeat(${authGridColumns}, minmax(0, 1fr))`
        } : undefined,
        children: authStrategies.map(strategy_1 => _jsx(AuthButton, {
          strategy: strategy_1,
          onClick: () => handleConnect(strategy_1),
          isLoading: isLoading
        }, strategy_1))
      }), _jsx("div", {
        className: "mb-4 w-full space-y-2",
        children: initialWallets.map(walletId_0 => _jsx(WalletRow, {
          walletId: walletId_0,
          onClick: () => handleConnect(walletId_0),
          isLoading: isLoading
        }, walletId_0))
      }), additionalWallets.length > 0 && _jsxs("div", {
        className: "w-full",
        children: [_jsx(Button, {
          onClick: () => setShowAllWallets(!showAllWallets),
          className: "mb-2 w-full bg-transparent text-gray-600 hover:bg-gray-100",
          children: showAllWallets ? "Show less" : "More options"
        }), showAllWallets && _jsx("div", {
          className: "max-h-60 space-y-2 overflow-y-auto",
          children: additionalWallets.map(walletId_1 => _jsx(WalletRow, {
            walletId: walletId_1,
            onClick: () => handleConnect(walletId_1),
            isLoading: isLoading
          }, walletId_1))
        })]
      })]
    })
  });
}